import { useEffect, useMemo, useState } from 'react'
import { AnimatePresence } from 'framer-motion'

import { buildConfettiPieces } from './helpers'
import { ConfettiOverlay, ConfettiOrigin, Piece } from './styled'
import { ConfettiBurstProps } from './types'

const DEFAULT_PIECE_COUNT = 80
const DEFAULT_SPREAD = 60
const DEFAULT_DURATION = 1800

/**
 * Tiny dependency-free confetti burst. Mounts a fixed-position overlay
 * centered on the viewport that emits N pieces fanning upward and
 * outward, falling into a gravity-shaped curve, then fading.
 *
 * Re-trigger by passing a new `trigger` value (typically `Date.now()`
 * or a counter). Setting `trigger` to 0 leaves the overlay invisible.
 *
 * @param {ConfettiBurstProps} props
 * @returns {JSX.Element | null}
 */
const ConfettiBurst = ({
  trigger,
  pieceCount = DEFAULT_PIECE_COUNT,
  spreadDegrees = DEFAULT_SPREAD,
  durationMs = DEFAULT_DURATION,
}: ConfettiBurstProps): JSX.Element | null => {
  const [activeTrigger, setActiveTrigger] = useState<number>(0)

  useEffect(() => {
    if (trigger === 0) {
      return
    }

    setActiveTrigger(trigger)
    const handle = setTimeout(() => {
      setActiveTrigger(0)
    }, durationMs + 300)

    return () => {
      clearTimeout(handle)
    }
  }, [trigger, durationMs])

  const pieces = useMemo(() => {
    if (activeTrigger === 0) {
      return []
    }

    return buildConfettiPieces(
      activeTrigger,
      pieceCount,
      spreadDegrees,
      durationMs,
    )
  }, [activeTrigger, pieceCount, spreadDegrees, durationMs])

  if (activeTrigger === 0) {
    return null
  }

  return (
    <AnimatePresence>
      <ConfettiOverlay key={activeTrigger}>
        <ConfettiOrigin>
          {pieces.map(piece => {
            const width =
              piece.shape === 'rectangle' || piece.shape === 'streamer'
                ? piece.size * 1.6
                : piece.size
            const height =
              piece.shape === 'streamer' ? piece.size / 2 : piece.size

            return (
              <Piece
                key={piece.id}
                shape={piece.shape}
                style={{
                  width: `${width}px`,
                  height: `${height}px`,
                  backgroundColor: piece.color,
                }}
                initial={{
                  x: piece.startX,
                  y: piece.startY,
                  rotate: piece.rotateStart,
                  opacity: 1,
                }}
                animate={{
                  x: piece.endX,
                  y: piece.endY,
                  rotate: piece.rotateEnd,
                  opacity: 0,
                  transition: {
                    duration: piece.durationMs / 1000,
                    delay: piece.delayMs / 1000,
                    ease: [0.18, 0.6, 0.4, 1],
                  },
                }}
              />
            )
          })}
        </ConfettiOrigin>
      </ConfettiOverlay>
    </AnimatePresence>
  )
}

export default ConfettiBurst
